import { ROLES, type Permission } from './permissions.js';

/** N0 = máxima autoridad. Número menor = más privilegio. */
export const ROLE_HIERARCHY_LEVELS: Record<string, number> = {
  [ROLES.DEVELOPER]: 0,
  [ROLES.SUPER_ADMIN]: 1,
  [ROLES.ADMIN]: 2,
  [ROLES.TEACHER]: 3,
  [ROLES.STUDENT]: 4,
};

export const MIN_ROLE_HIERARCHY_LEVEL = 0;
export const DEFAULT_CUSTOM_ROLE_LEVEL = 5;

export function hasFullSystemAccess(roles: readonly string[]): boolean {
  return roles.includes(ROLES.SUPER_ADMIN) || roles.includes(ROLES.DEVELOPER);
}

export function roleHierarchyLevel(roleName: string): number {
  return ROLE_HIERARCHY_LEVELS[roleName] ?? DEFAULT_CUSTOM_ROLE_LEVEL;
}

export function resolveRoleHierarchyLevel(role: {
  name: string;
  hierarchyLevel?: number | null;
}): number {
  if (role.hierarchyLevel != null && role.hierarchyLevel >= MIN_ROLE_HIERARCHY_LEVEL) {
    return role.hierarchyLevel;
  }
  return roleHierarchyLevel(role.name);
}

export function hierarchyLevelLabel(level: number): string {
  return `N${level}`;
}

export function userHierarchyLevel(roleNames: readonly string[]): number {
  if (roleNames.length === 0) return DEFAULT_CUSTOM_ROLE_LEVEL;
  return Math.min(...roleNames.map(roleHierarchyLevel));
}

export function resolveUserHierarchyLevel(
  roleNames: readonly string[],
  roleLevels?: ReadonlyMap<string, number | null | undefined>,
): number {
  if (roleNames.length === 0) return DEFAULT_CUSTOM_ROLE_LEVEL;
  return Math.min(
    ...roleNames.map((name) =>
      resolveRoleHierarchyLevel({ name, hierarchyLevel: roleLevels?.get(name) }),
    ),
  );
}

/** Puede gestionar (crear/editar/eliminar) usuarios estrictamente inferiores en jerarquía. */
export function canManageUserByHierarchy(
  actorRoles: readonly string[],
  targetUserRoles: readonly string[],
  options?: {
    actorLevel?: number;
    targetRoleLevels?: ReadonlyMap<string, number | null | undefined>;
  },
): boolean {
  if (actorRoles.includes(ROLES.DEVELOPER)) return true;
  const actorHierarchy = options?.actorLevel ?? userHierarchyLevel(actorRoles);
  const targetHierarchy = resolveUserHierarchyLevel(
    targetUserRoles,
    options?.targetRoleLevels,
  );
  return actorHierarchy < targetHierarchy;
}

/** Puede gestionar (editar, eliminar, asignar permisos) roles estrictamente inferiores. */
export function canManageRoleByHierarchy(
  actorRoles: readonly string[],
  targetRole: string | { name: string; hierarchyLevel?: number | null },
  actorLevel?: number,
): boolean {
  const target =
    typeof targetRole === 'string'
      ? { name: targetRole, hierarchyLevel: undefined }
      : targetRole;
  const targetLevel = resolveRoleHierarchyLevel(target);
  const actorHierarchy = actorLevel ?? userHierarchyLevel(actorRoles);

  if (actorRoles.includes(ROLES.DEVELOPER)) return true;
  if (
    actorRoles.includes(ROLES.SUPER_ADMIN) &&
    targetLevel >= roleHierarchyLevel(ROLES.SUPER_ADMIN)
  ) {
    return true;
  }

  return actorHierarchy < targetLevel;
}

/** Puede asignar un rol a un usuario (solo niveles estrictamente inferiores al actor). */
export function canAssignRoleByHierarchy(
  actorRoles: readonly string[],
  targetRole: string | { name: string; hierarchyLevel?: number | null },
  actorLevel?: number,
): boolean {
  if (actorRoles.includes(ROLES.DEVELOPER)) return true;
  const target =
    typeof targetRole === 'string'
      ? { name: targetRole, hierarchyLevel: undefined }
      : targetRole;
  const targetLevel = resolveRoleHierarchyLevel(target);
  const actorHierarchy = actorLevel ?? userHierarchyLevel(actorRoles);
  return isHierarchyLevelAssignable(actorHierarchy, targetLevel);
}

export function canAssignRolesByHierarchy(
  actorRoles: readonly string[],
  roleNames: readonly string[],
  options?: {
    actorLevel?: number;
    targetLevels?: ReadonlyMap<string, number | null | undefined>;
  },
): boolean {
  const actorLevel = options?.actorLevel ?? userHierarchyLevel(actorRoles);
  return roleNames.every((name) =>
    canAssignRoleByHierarchy(
      actorRoles,
      { name, hierarchyLevel: options?.targetLevels?.get(name) },
      actorLevel,
    ),
  );
}

export function isHierarchyLevelAssignable(actorLevel: number, targetLevel: number): boolean {
  return targetLevel > actorLevel;
}

export function filterManageableRoleNames(
  actorRoles: readonly string[],
  roleNames: readonly string[],
  options?: {
    actorLevel?: number;
    targetLevels?: ReadonlyMap<string, number | null | undefined>;
  },
): string[] {
  return roleNames.filter((name) => {
    const hierarchyLevel = options?.targetLevels?.get(name);
    return canManageRoleByHierarchy(actorRoles, { name, hierarchyLevel }, options?.actorLevel);
  });
}

export function canGrantPermissions(
  actor: { roles: readonly string[]; permissions: readonly Permission[] },
  permissionsToGrant: readonly string[],
): boolean {
  if (hasFullSystemAccess(actor.roles)) return true;
  const granted = new Set(actor.permissions);
  return permissionsToGrant.every((permission) => granted.has(permission as Permission));
}

export function sortRolesByHierarchy<T extends { name: string; hierarchyLevel?: number | null }>(
  roles: readonly T[],
): T[] {
  return [...roles].sort((a, b) => {
    const diff = resolveRoleHierarchyLevel(a) - resolveRoleHierarchyLevel(b);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name, 'es');
  });
}
